import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Filesystem, Directory, WriteFileResult, FileInfo, StatResult, ReadFileResult, GetUriResult, ReaddirResult } from '@capacitor/filesystem';
import { Observable, from, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Platform } from '@ionic/angular';
import { map, catchError, switchMap } from 'rxjs/operators';
import { AUTHENTICATED } from 'src/app/oidc/auth.interceptor';

export interface ITicket {
  semesterId: string;
  ticketData: string;
}

export class MetaTicket {
  name: string;
  isLocal: boolean;
  isOnline: boolean;
  isDownloading: boolean;
  isCode: boolean;
  isPassbook: boolean;

  constructor(name: string, isLocal: boolean, isOnline: boolean, isCode: boolean = false, isPassbook: boolean = false) {
    this.name = name;
    this.isLocal = isLocal;
    this.isOnline = isOnline;
    this.isDownloading = false;
    this.isCode = isCode;
    this.isPassbook = isPassbook;
  }
}

const API_URL = environment.apiUrl;
// const API_SERVICE = `http://localhost:8090`;
// const API_SERVICE = `http://10.0.2.2:9080`; // Android emulator
const API_SERVICE = `${API_URL}/ticket/v4`;
const API_TICKETS = `${API_SERVICE}/tickets`;
const API_TICKET = `${API_SERVICE}/tickets/{0}`;
const API_TICKET_CODE = `${API_SERVICE}/tickets/{0}/code`;
const API_TICKET_PASSBOOK = `${API_SERVICE}/tickets/{0}/passbook`;

@Injectable({
  providedIn: 'root',
})
export class TicketService {
  private http = inject(HttpClient);
  private platform = inject(Platform);

  private directory: Directory;

  constructor() {
    this.directory = this.platform.is('ios') ? Directory.Documents : Directory.Data;
  }

  public ticketList(): Observable<string[]> {
    return this.http.get<string[]>(API_TICKETS, { context: AUTHENTICATED });
  }

  public ticket(semesterId: string): Observable<ITicket> {
    return this.http.get<ITicket>(API_TICKET.format(semesterId), {
      context: AUTHENTICATED,
    });
  }

  public ticketCode(semesterId: string): Observable<ITicket> {
    return this.http.get<ITicket>(API_TICKET_CODE.format(semesterId), {
      context: AUTHENTICATED,
    });
  }

  public ticketPassbook(semesterId: string): Observable<ITicket> {
    return this.http.get<ITicket>(API_TICKET_PASSBOOK.format(semesterId), {
      context: AUTHENTICATED,
    });
  }

  public saveTicket(ticket: ITicket): Observable<WriteFileResult> {
    if (ticket.semesterId === null || ticket.semesterId === undefined) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.writeFile({
        directory: this.directory,
        data: ticket.ticketData,
        path: `tickets/${ticket.semesterId}.png`,
        recursive: true,
      })
    );
  }

  public saveTicketCode(ticket: ITicket): Observable<WriteFileResult> {
    if (!ticket.semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.stat({
        path: 'tickets',
        directory: this.directory,
      })
    ).pipe(
      switchMap((stats: StatResult) => {
        if (stats.type === 'directory') {
          return from(
            Filesystem.writeFile({
              directory: this.directory,
              data: ticket.ticketData,
              path: `tickets/${ticket.semesterId}_code.png`,
            })
          );
        } else {
          return from(
            Filesystem.mkdir({
              path: 'tickets',
              directory: this.directory,
              recursive: true,
            })
          ).pipe(
            switchMap(() =>
              from(
                Filesystem.writeFile({
                  directory: this.directory,
                  data: ticket.ticketData,
                  path: `tickets/${ticket.semesterId}_code.png`,
                })
              )
            ),
            catchError((error) =>
              from(
                Filesystem.writeFile({
                  directory: this.directory,
                  data: ticket.ticketData,
                  path: `tickets/${ticket.semesterId}_code.png`,
                })
              )
            )
          );
        }
      }),
      catchError(() =>
        from(
          Filesystem.mkdir({
            path: 'tickets',
            directory: this.directory,
            recursive: true,
          })
        ).pipe(
          switchMap(() =>
            from(
              Filesystem.writeFile({
                directory: this.directory,
                data: ticket.ticketData,
                path: `tickets/${ticket.semesterId}_code.png`,
              })
            )
          ),
          catchError((error) =>
            from(
              Filesystem.writeFile({
                directory: this.directory,
                data: ticket.ticketData,
                path: `tickets/${ticket.semesterId}_code.png`,
              })
            )
          )
        )
      )
    );
  }

  public saveTicketPassbook(ticket: ITicket): Observable<WriteFileResult> {
    if (!ticket.semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.stat({
        path: 'tickets',
        directory: this.directory,
      })
    ).pipe(
      switchMap((stats: StatResult) => {
        if (stats.type === 'directory') {
          return from(
            Filesystem.writeFile({
              directory: this.directory,
              data: ticket.ticketData,
              path: `tickets/${ticket.semesterId}_passbook.pkpass`,
            })
          );
        } else {
          return from(
            Filesystem.mkdir({
              path: 'tickets',
              directory: this.directory,
              recursive: true,
            })
          ).pipe(
            switchMap(() =>
              from(
                Filesystem.writeFile({
                  directory: this.directory,
                  data: ticket.ticketData,
                  path: `tickets/${ticket.semesterId}_passbook.pkpass`,
                })
              )
            ),
            catchError((error) =>
              from(
                Filesystem.writeFile({
                  directory: this.directory,
                  data: ticket.ticketData,
                  path: `tickets/${ticket.semesterId}_passbook.pkpass`,
                })
              )
            )
          );
        }
      }),
      catchError(() =>
        from(
          Filesystem.mkdir({
            path: 'tickets',
            directory: this.directory,
            recursive: true,
          })
        ).pipe(
          switchMap(() =>
            from(
              Filesystem.writeFile({
                directory: this.directory,
                data: ticket.ticketData,
                path: `tickets/${ticket.semesterId}_passbook.pkpass`,
              })
            )
          ),
          catchError((error) =>
            from(
              Filesystem.writeFile({
                directory: this.directory,
                data: ticket.ticketData,
                path: `tickets/${ticket.semesterId}_passbook.pkpass`,
              })
            )
          )
        )
      )
    );
  }

  public removeTicket(semesterId: string): Observable<boolean> {
    if (semesterId === null || semesterId === undefined) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.deleteFile({
        directory: this.directory,
        path: `tickets/${semesterId}.png`,
      })
    ).pipe(
      map(() => true),
      catchError((err) => of(false))
    );
  }

  public removeTicketCode(semesterId: string): Observable<boolean> {
    if (!semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.deleteFile({
        directory: this.directory,
        path: `tickets/${semesterId}_code.png`,
      })
    ).pipe(
      map(() => true),
      catchError(() => {
        return of(false);
      })
    );
  }

  public removeTicketPassbook(semesterId: string): Observable<boolean> {
    if (!semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.deleteFile({
        directory: this.directory,
        path: `tickets/${semesterId}_passbook.pkpass`,
      })
    ).pipe(
      map(() => true),
      catchError((err) => {
        return of(false);
      })
    );
  }

  public removeAllTickets(): Observable<boolean> {
    return from(
      Filesystem.rmdir({
        directory: this.directory,
        path: 'tickets',
        recursive: true,
      })
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  public getLocalTicketFiles(): Observable<string[]> {
    return from(
      Filesystem.stat({
        directory: this.directory,
        path: 'tickets',
      })
    ).pipe(
      catchError(() => {
        return of([]);
      }),
      switchMap((stat: StatResult) => {
        if(stat.type !== 'directory') {
          return of([]);
        }

        return from(
          Filesystem.readdir({
            directory: this.directory,
            path: 'tickets',
          })
        ).pipe(
          map((directory: ReaddirResult) => {
            return directory?.files || [];
          }),
          map((fileInfos: FileInfo[]) => fileInfos.map(fileInfo => fileInfo.name)),
          map((filenames: string[]) => {
            return filenames
              .filter((str: string) => {
                return /^\d{5}(_code|_passbook)?\.(png|pkpass)$/.test(str);
              })
              .map(filename => filename.substring(0, filename.lastIndexOf('.')));
          })
        );
      })
    );
  }

  public getTicket(semesterId: string): Observable<string> {
    if (semesterId === null || semesterId === undefined) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      // Use of readFile (instead of readURI) because readFile supports PWA Storage in the IndexedDB
      Filesystem.readFile({
        directory: this.directory,
        path: `tickets/${semesterId}.png`,
      })
    ).pipe(map((file: ReadFileResult) => `data:image/png;base64, ${file.data.toString()}`));
  }

  public getTicketCode(semesterId: string): Observable<string> {
    if (!semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      // Use of readFile (instead of readURI) because readFile supports PWA Storage in the IndexedDB
      Filesystem.readFile({
        directory: this.directory,
        path: `tickets/${semesterId}_code.png`,
      })
    ).pipe(map((file: ReadFileResult) => `data:image/png;base64,${file.data.toString()}`));
  }

  public getTicketPassbook(semesterId: string): Observable<string> {
    if (!semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      // Use of readFile (instead of readURI) because readFile supports PWA Storage in the IndexedDB
      Filesystem.readFile({
        directory: this.directory,
        path: `tickets/${semesterId}_passbook.pkpass`,
      })
    ).pipe(map((file: ReadFileResult) => `data:application/vnd.apple.pkpass;base64,${file.data.toString()}`));
  }

  public getTicketPassbookURI(semesterId: string): Observable<string> {
    if (!semesterId) {
      throw new Error('semesterId is null or undefined');
    }

    return from(
      Filesystem.getUri({
        directory: this.directory,
        path: `tickets/${semesterId}_passbook.pkpass`,
      })
    ).pipe(map((uri: GetUriResult) => uri.uri));
  }

  public getLocalTickets(): Observable<MetaTicket[]> {
    return this.getLocalTicketFiles().pipe(
      switchMap((localTicketFiles: string[]) => {
        return of(this.processLocalTickets(localTicketFiles));
      })
    )
  }

  public processLocalTickets(localTickets: string[]): MetaTicket[] {
    return localTickets.reduce<MetaTicket[]>((acc, currentTicket) => {
      const isCode = currentTicket.endsWith('_code');
      const isPassbook = currentTicket.endsWith('_passbook');
      const isLocal = !isCode && !isPassbook;

      let semesterId = this.extractSemesterId(currentTicket);
      const ticketIndex = acc.findIndex((ticket) => ticket.name === semesterId);

      if (ticketIndex === -1) {
        acc.push(new MetaTicket(semesterId, isLocal, false, isCode, isPassbook));
      } else {
        const existingTicket = acc[ticketIndex];
        existingTicket.isLocal = existingTicket.isLocal || isLocal;
        existingTicket.isCode = existingTicket.isCode || isCode;
        existingTicket.isPassbook = existingTicket.isPassbook || isPassbook;
      }

      return acc;
    }, []);
  }

  private extractSemesterId(ticketName: string): string | null {
    const match = ticketName.match(/^\d{5}/);
    return match ? match[0] : null;
  }
}
